/* eslint-disable max-lines */
import { IAvailableFulfillmentOptionsResponse } from '../../models/requests/omm';
import { ILineItem } from '../../models/common';
import {
  IFulfillmentGroup,
  IOrderDetailsResponse,
} from '../../models/order-details';
import {
  CANCELLATION_STATUS,
  FULFILLMENT_TYPE,
  STATUS_CODE,
  STATUS_DESCRIPTION,
} from '../../constants/status';
import { IFulfillmentStatus } from '../../components/order-details/util/FulfillmentStatus';
import { retrieveTrackingNumbers } from '../returns/returns-helper';
import { dateFormat } from '../../components/order-details/util/format';
import {
  longWeekDayMonthDay,
  monthDay,
  weekDayMonthDay,
} from '../../constants/dateStrings';
import { normalize, formatPhoneNumber } from '../general';
import {
  IDeliveryAddress,
  IDeliveryOption,
} from '../../models/edit-delivery-address';
import { formatDateForDelivery } from '../edit-delivery-address/calendar';

const ONLINE = 'ONLINE';
const INSTORE = 'INSTORE';

export const getOrderType = (orderNumber: string): 'ONLINE' | 'INSTORE' => {
  if (orderNumber && (orderNumber[0] === 'W' || orderNumber[0] === 'C')) {
    return ONLINE;
  }
  return INSTORE;
};

/**
 * Expected Output:
 * Initiated Returns (SORIS) -> "Return by (return by date)"
 * Initiated Returns (UPS) -> "Return to UPS by (return by date)"
 * Completed Returns -> "Return Completed (return completion date)"
 * @returns {string} - The display header for initiated and completed returns
 */
export const getReturnsDisplayHeader = (
  firstItem?: ILineItem,
  isReturnInitiated?: boolean,
  isReturnCompleted?: boolean,
  isReturnInProgress?: boolean,
  isPickupOrLORP?: boolean,
  isSoris?: boolean,
  enableReturnsScheduledPickup?: boolean,
) => {
  if (isReturnInitiated) {
    if (isPickupOrLORP) {
      return firstItem?.returnPickupDate && enableReturnsScheduledPickup
        ? `Return Pickup Scheduled for ${dateFormat(
            firstItem.returnPickupDate,
            longWeekDayMonthDay,
          )}`
        : 'Return Pickup Requested';
    }
    if (!firstItem?.returnByDate || firstItem.returnByDate === 'EXPIRED')
      return 'Return Initiated';
    if (isSoris) {
      return `Return in-store by ${dateFormat(
        firstItem.returnByDate,
        weekDayMonthDay,
      )}`;
    }
    return `Return to UPS by ${dateFormat(
      firstItem.returnByDate,
      weekDayMonthDay,
    )}`;
  }
  if (isReturnCompleted) {
    if (!firstItem?.statusUpdatedDate) return 'Return Completed';

    return `Return Completed ${dateFormat(
      firstItem?.statusUpdatedDate,
      monthDay,
      true,
    )}`;
  }
  if (isReturnInProgress) {
    if (!firstItem?.statusUpdatedDate) return 'Received by UPS';
    return `Received by UPS ${dateFormat(
      firstItem?.statusUpdatedDate,
      monthDay,
      true,
    )}`;
  }
  return '';
};

export const getDisplayHeader = (
  fg: IFulfillmentGroup,
  enableReturnsScheduledPickup?: boolean,
) => {
  const { fulfillmentType, lineItems } = fg;
  let header;
  if (fulfillmentType) {
    if (fulfillmentType === FULFILLMENT_TYPE.ISP) {
      header = 'In-Store Purchase';
    }
    if (
      [
        FULFILLMENT_TYPE.STH,
        FULFILLMENT_TYPE.BODFS,
        FULFILLMENT_TYPE.APPLIANCE,
        FULFILLMENT_TYPE.DFS,
      ].includes(fulfillmentType as FULFILLMENT_TYPE)
    ) {
      header = 'Delivery';
    }
    if (
      [
        FULFILLMENT_TYPE.BOPIS,
        FULFILLMENT_TYPE.BOSS,
        FULFILLMENT_TYPE.WILLCALL,
      ].includes(fulfillmentType as FULFILLMENT_TYPE)
    ) {
      header = 'Pick Up';
    }
    if (fulfillmentType === FULFILLMENT_TYPE.MISC) header = 'Miscellaneous';
    if (fulfillmentType === FULFILLMENT_TYPE.CANCEL) header = 'Canceled Items';

    /* 
    if the order is returned or return is initiated, the fulfillmentType doesnot get updated, 
    so we need to check the lineItems so see what is the statusCode to update the displayHeader
    */
    const {
      isInitiatedReturn,
      isCompleteReturn,
      isInProgressReturn,
      isReturnedMultiReturn,
      isJobSitePickup,
      isSoris,
    } = getReturnStatus(fg);

    if (isReturnedMultiReturn) {
      const trackingNumbers = retrieveTrackingNumbers(fg);
      const isReturnPickUp = isReturnOrderAPickUp(
        trackingNumbers,
        isJobSitePickup,
      );
      return getReturnsDisplayHeader(
        lineItems?.[0],
        isInitiatedReturn,
        isCompleteReturn,
        isInProgressReturn,
        isReturnPickUp,
        isSoris,
        enableReturnsScheduledPickup,
      );
    }

    if (isInitiatedReturn) {
      header = 'Return Initiated';
    } else if (isInProgressReturn) {
      header = 'Received by UPS';
    } else if (isCompleteReturn) {
      header = 'Return Completed';
    } else if (fulfillmentType) {
      switch (fulfillmentType) {
        case FULFILLMENT_TYPE.ISP:
          header = 'In-Store Purchase';
          break;
        case FULFILLMENT_TYPE.STH:
        case FULFILLMENT_TYPE.BODFS:
        case FULFILLMENT_TYPE.APPLIANCE:
        case FULFILLMENT_TYPE.DFS:
          header = 'Delivery';
          break;
        case FULFILLMENT_TYPE.BOPIS:
        case FULFILLMENT_TYPE.BOSS:
          header = 'Pick Up';
          break;
        case FULFILLMENT_TYPE.MISC:
          header = 'Miscellaneous';
          break;
        case FULFILLMENT_TYPE.CANCEL:
          header = 'Canceled Items';
          break;
        case FULFILLMENT_TYPE.ADDITIONAL_ITEMS:
          header = 'Additional Items';
          break;
        default:
          break;
      }
    }
  }
  return header;
};

export const hasBackorderedItem = (fulfillmentGroup: IFulfillmentGroup) => {
  const numOfBOItems =
    fulfillmentGroup.lineItems?.filter(
      (item) => item.statusCode === STATUS_CODE.BACKORDERED,
    )?.length ?? 0;
  return numOfBOItems > 0;
};

export const hasDelayedShipmentItems = (
  fulfillmentGroup: IFulfillmentGroup,
) => {
  const numOfDelayedItems =
    fulfillmentGroup?.lineItems?.filter((item) => item.delayedShipment)
      ?.length ?? 0;
  return numOfDelayedItems > 0;
};

// this function separates the canceled items from the fulfillmentgroups from the response, to render them separately.
export const processFulfillmentGroup = (
  fulfillmentGroups: IFulfillmentGroup[],
) => {
  // Separate Canceled Items
  const updatedFulfillmentGroup = fulfillmentGroups.flatMap((group) => {
    const canceledLineItems = group?.lineItems?.filter(
      (item) => item.statusCode === 140,
    );
    const {
      cancellationStatus,
      oosRecoveryOptions,
      storePickup,
      fulfillmentType,
      actions,
      fulfillmentStore,
    } = group;
    const hasCanceledLineItem =
      canceledLineItems && canceledLineItems.length > 0;

    if (hasCanceledLineItem) {
      const canceledFulfillment =
        cancellationStatus === CANCELLATION_STATUS.OUT_OF_STOCK &&
        group.actions?.isOosRecoveryEnabled &&
        (group.oosRecoveryOptions?.length ?? 0) > 0
          ? {
              fulfillmentType,
              lineItems: canceledLineItems,
              cancellationStatus,
              oosRecoveryOptions,
              storePickup,
              actions,
              fulfillmentStore,
            }
          : {
              fulfillmentType: FULFILLMENT_TYPE.CANCEL,
              lineItems: canceledLineItems,
            };

      const remainingLineItems = group?.lineItems?.filter(
        (item) => item.statusCode !== 140,
      );

      if (remainingLineItems && remainingLineItems.length > 0) {
        const remainingFulfillment = {
          ...group,
          lineItems: remainingLineItems,
        };

        return [remainingFulfillment, canceledFulfillment];
      }

      return [canceledFulfillment];
    }

    return [group];
  });

  return updatedFulfillmentGroup;
};

// Indexing Cancellation Items fulfillmentType to last Index of the fulfillment Array
export const moveCanceledObjectToLastIndex = (
  fulfillmentTypeArray: IFulfillmentGroup[],
) => {
  const canceledItems = fulfillmentTypeArray?.filter?.(
    (item) => item.fulfillmentType === FULFILLMENT_TYPE.CANCEL,
  );

  const filteredOutArray = fulfillmentTypeArray?.filter?.(
    (item) => item?.fulfillmentType !== FULFILLMENT_TYPE.CANCEL,
  );
  return [...filteredOutArray, ...canceledItems];
};

/**
 * Orders the provided array by the dates of the fulfillment groups
 */
export const orderReturnFGByDate = (fulfillmentGroups: IFulfillmentGroup[]) => {
  fulfillmentGroups.sort((fgA, fgB) => {
    const dateA = new Date(fgA.returnDate || '');
    const dateB = new Date(fgB.returnDate || '');

    if (dateA < dateB) return 1;
    if (dateA > dateB) return -1;
    return 0;
  });
};
/**
 * Util function for returning boolean checks for the status (returned, return initiated, etc) of the fulfillment group
 * This also uses the returnDate, since it's a field that is only added when it's a fg that comes from the multi-return changes.
 */
export const getReturnStatus = (fulfillmentGroup: IFulfillmentGroup) => {
  const status = (fulfillmentGroup?.lineItems?.[0]?.statusDescription ||
    '') as STATUS_DESCRIPTION;
  const isJobSitePickup = fulfillmentGroup?.lineItems?.[0]?.isJobSitePickup;
  const isCompleteReturn = status === STATUS_DESCRIPTION.RETURNED;
  const isInitiatedReturn = status === STATUS_DESCRIPTION.RETURN_INITIATED;
  const isInProgressReturn = status === STATUS_DESCRIPTION.RETURN_IN_PROGRESS;
  const isReturnStatus = [
    STATUS_DESCRIPTION.RETURNED,
    STATUS_DESCRIPTION.RETURN_INITIATED,
    STATUS_DESCRIPTION.RETURN_IN_PROGRESS,
  ].includes(status);

  const isReturnedMultiReturn =
    isReturnStatus && fulfillmentGroup.returnDate !== undefined;
  const isReturnedPreMultiReturn =
    isReturnStatus && fulfillmentGroup.returnDate === undefined;
  const isSoris = fulfillmentGroup?.isSoris || false;
  return {
    isCompleteReturn,
    isInitiatedReturn,
    isInProgressReturn,
    isReturnedMultiReturn,
    isReturnedPreMultiReturn,
    isSoris,
    isJobSitePickup,
  };
};
/**
 * Determines whether the print return label is enabled for a fulfillment group.
 *
 * @param fulfillmentGroup - The fulfillment group of the order.
 * @param fulfillmentStatus - The fulfillment status of the order.
 * @param isInitiatedReturn - Indicates whether a return has been initiated for the order.
 * @param trackingNumbers - The tracking numbers associated with the order.
 * @param isSoris - Indicates whether the order is a SORIS order.
 * @returns A boolean value indicating whether the print return label is enabled.
 */
export const isPrintReturnLabelEnabled = (
  fulfillmentGroup: IFulfillmentGroup,
  fulfillmentStatus: IFulfillmentStatus,
  isInitiatedReturn: boolean,
  trackingNumbers: string[],
  isSoris = false,
) => {
  if (isSoris) return false;

  // print label is not necessary for pickup order
  const isPickUp = isUPSPickup(trackingNumbers);
  const upsTrackingNumbers = trackingNumbers.filter((trackingInfo) =>
    trackingInfo.startsWith('1Z'),
  );
  const containsNonUpsNumber =
    trackingNumbers.length !== upsTrackingNumbers.length;

  // although all items in the group should contain the same returnByDate value, we are checking all items to be safe as we dont want to prevent someone from printing a label for any possible items that are still within the return window
  const isFulfillmentReturnWindowExpired = fulfillmentGroup?.lineItems?.every(
    (lineItem) => lineItem.returnByDate === 'EXPIRED',
  );
  const printReturnLabelEnabled =
    fulfillmentStatus.isFulfillmentSTHorBOSS &&
    isInitiatedReturn &&
    !isFulfillmentReturnWindowExpired &&
    upsTrackingNumbers &&
    upsTrackingNumbers.length > 0 &&
    !containsNonUpsNumber &&
    !isPickUp;

  return printReturnLabelEnabled;
};

export const getEjKey = (
  orderDetails?: IOrderDetailsResponse,
  isB2CCustomer?: boolean,
) => {
  const ejDate = orderDetails?.salesDate;
  const ejRegister = orderDetails?.registerNumber?.toString().padStart(2, '0');
  const ejStore = orderDetails?.storeNumber?.toString().padStart(4, '0');

  // For post-CPS B2B orders, the backend returns `transactionIdWithCheckDigit`
  // (the full 5-digit id). When present, derive ejSeqNo from it by stripping
  // the trailing check digit. Pre-CPS B2B orders fall back to the legacy
  // behavior where `transactionId` itself contained the check digit.
  const b2bEjSeqNoSource =
    orderDetails?.transactionIdWithCheckDigit ?? orderDetails?.transactionId;

  const ejSeqNo = isB2CCustomer
    ? orderDetails?.transactionId?.toString().padStart(4, '0')
    : b2bEjSeqNoSource?.toString().padStart(5, '0').slice(0, 4);

  return { ejRegister, ejSeqNo, ejStore, ejDate };
};

export const isUPSPickup = (trackingNumbers: string[]) => {
  // returns true if any of the tracking numbers are for an order being picked up by UPS
  const upsTrackingNumbers = trackingNumbers.filter((trackingInfo) =>
    trackingInfo.startsWith('1Z'),
  );
  return upsTrackingNumbers?.some(
    (trackingInfo) =>
      trackingInfo.substring(8, 10) === '78' ||
      trackingInfo.substring(8, 10) === '26',
  );
};
export const isLORPReturn = (trackingNumbers: string[]) => {
  return trackingNumbers?.some((trackingInfo) => trackingInfo?.[0] === 'R');
};

/**
 * Checks if the order is a pickup order which currently includes:
 * - Jobsite Pickup
 * - LORP (Large Order Return Pickup)
 * - UPS Pickup
 * @returns {boolean} - Returns true if the order is a pickup order, false otherwise.
 */
export const isReturnOrderAPickUp = (
  trackingNumbers: string[],
  isJobSitePickup?: boolean,
) =>
  isJobSitePickup ||
  isUPSPickup(trackingNumbers) ||
  isLORPReturn(trackingNumbers);

/**
 * Util function to build the delivery tracker lineItem request object, non STH ones will not have a lineItem field
 * @param fulfillmentGroup standard FG
 * @returns lineItem request object for STH fulfillment types required for delivery tracker request
 */
export const buildSTHDeliveryTrackerItems = (
  fulfillmentGroup: IFulfillmentGroup,
) => {
  if (fulfillmentGroup?.fulfillmentType !== FULFILLMENT_TYPE.STH)
    return undefined;
  return fulfillmentGroup?.lineItems?.map((lineItem) => ({
    lineId: lineItem.lineId,
    returnWindow: lineItem.returnWindow,
  }));
};

/**
 * Compares two address objects to determine if they are equal.
 * Only compares addressLine1, addressLine2, and zipCode fields after normalization.
 *
 * @returns {boolean} True if the addresses are equal, false otherwise.
 */
export function areAddressesEqual(
  firstAddress: {
    firstName?: string | null;
    lastName?: string | null;
    phoneNumber?: string | null;
    city?: string | null;
    state?: string | null;
    addressLine1?: string | null;
    addressLine2?: string | null;
    zipCode?: string | null;
    country?: string | null;
  },
  secondAddress: {
    firstName?: string | null;
    lastName?: string | null;
    phoneNumber?: string | null;
    city?: string | null;
    state?: string | null;
    addressLine1?: string | null;
    addressLine2?: string | null;
    zipCode?: string | null;
    country?: string | null;
  },
): boolean {
  return (
    normalize(firstAddress.firstName) === normalize(secondAddress.firstName) &&
    normalize(firstAddress.lastName) === normalize(secondAddress.lastName) &&
    normalize(firstAddress.phoneNumber) ===
      normalize(secondAddress.phoneNumber) &&
    normalize(firstAddress.city) === normalize(secondAddress.city) &&
    normalize(firstAddress.state) === normalize(secondAddress.state) &&
    normalize(firstAddress.addressLine1) ===
      normalize(secondAddress.addressLine1) &&
    normalize(firstAddress.addressLine2) ===
      normalize(secondAddress.addressLine2) &&
    normalize(firstAddress.zipCode) === normalize(secondAddress.zipCode)
  );
}

/**
 * Extracts and normalizes address data from a fulfillment group source.
 *
 * @param {IFulfillmentGroup['shippingAndHandling'] | IFulfillmentGroup['storePickup'] | IFulfillmentGroup['bodfsDeliveryAndHandling']} addressSource
 *   The specific address source object (shippingAndHandling, storePickup, or bodfsDeliveryAndHandling).
 * @param {string} fulfillmentType - The fulfillment type (e.g., 'sth', 'bopis', 'bodfs').
 */
export function extractAddressFromFulfillmentGroup(
  addressSource:
    | IFulfillmentGroup['shippingAndHandling']
    | IFulfillmentGroup['storePickup']
    | IFulfillmentGroup['bodfsDeliveryAndHandling'],
  fulfillmentType: string,
) {
  switch (fulfillmentType) {
    case FULFILLMENT_TYPE.STH: {
      const delivery = (
        addressSource as IFulfillmentGroup['shippingAndHandling']
      )?.deliveryAddress;

      return {
        firstName: delivery?.name?.firstName ?? '',
        lastName: delivery?.name?.lastName ?? '',
        addressLine1: delivery?.postalDetails?.addressLine1 ?? '',
        addressLine2: delivery?.postalDetails?.addressLine2 ?? null,
        city: delivery?.postalDetails?.city ?? '',
        state: delivery?.postalDetails?.state ?? '',
        zipCode: delivery?.postalDetails?.zipCode ?? '',
        country: delivery?.postalDetails?.country ?? '',
        phoneNumber: formatPhoneNumber(delivery?.phoneNumbers?.dayPhone || ''),
      };
    }

    case FULFILLMENT_TYPE.BODFS: {
      const delivery = (
        addressSource as IFulfillmentGroup['bodfsDeliveryAndHandling']
      )?.bodfsDeliveries?.[0]?.deliveryAddress;

      return {
        firstName: delivery?.name?.firstName ?? '',
        lastName: delivery?.name?.lastName ?? '',
        addressLine1: delivery?.postalDetails?.addressLine1 ?? '',
        addressLine2: delivery?.postalDetails?.addressLine2 ?? null,
        city: delivery?.postalDetails?.city ?? '',
        state: delivery?.postalDetails?.state ?? '',
        zipCode: delivery?.postalDetails?.zipCode ?? '',
        country: delivery?.postalDetails?.country ?? '',
        phoneNumber: formatPhoneNumber(delivery?.phoneNumbers?.dayPhone || ''),
      };
    }

    case FULFILLMENT_TYPE.BOPIS: {
      const pickup = addressSource as IFulfillmentGroup['storePickup'];
      const store = pickup?.store?.address;

      return {
        firstName: pickup?.pickUpPerson?.name?.firstName ?? '',
        lastName: pickup?.pickUpPerson?.name?.lastName ?? '',
        addressLine1: '',
        addressLine2: null,
        city: '',
        state: '',
        zipCode: '',
        country: store?.country ?? '',
        phoneNumber: formatPhoneNumber(
          pickup?.pickUpPerson?.phoneNumbers?.dayPhone || '',
        ),
      };
    }
    default:
      return {
        firstName: '',
        lastName: '',
        addressLine1: '',
        addressLine2: null,
        city: '',
        state: '',
        zipCode: '',
        country: '',
        phoneNumber: '',
      };
  }
}

/**
 * Checks if an address object is considered empty.
 * Only validates the fields: addressLine1, addressLine2, and zipCode.
 * Returns true if all these fields are empty or falsy.
 *
 */
export function isAddressEmpty(address: Partial<IDeliveryAddress>): boolean {
  if (!address) return true;
  return !address.addressLine1 && !address.addressLine2 && !address.zipCode;
}

export const buildDeliveryOptions = (
  fulfillmentOptionsResponse: IAvailableFulfillmentOptionsResponse | null,
): IDeliveryOption[] => {
  if (!fulfillmentOptionsResponse) return [];

  const optionsArray: IDeliveryOption[] = [];

  const fulfillmentOptions =
    fulfillmentOptionsResponse.data?.availableFulfillmentOptions?.delivery
      ?.fulfillmentOptions;

  const formatDeliveryCost = (charge: string, prefix = '$') =>
    charge === '0.00' ? 'FREE' : `${prefix}${charge}`;

  // STH
  if (fulfillmentOptions?.STH.isEligible) {
    const details = fulfillmentOptions.STH;
    const date = formatDateForDelivery(details.startDate, details.endDate);

    optionsArray.push({
      id: FULFILLMENT_TYPE.STH,
      title: `Get it by ${date.isSameDay ? date.fullDate : date.range}`,
      deliveryCost: formatDeliveryCost(details.deliveryCharge),
      rawDeliveryCharge: parseFloat(details.deliveryCharge),
      rawStartDate: new Date(details.startDate),
    });
  }

  // BODFS
  if (fulfillmentOptions?.BODFS.isEligible) {
    const details = fulfillmentOptions.BODFS;
    const date = formatDateForDelivery(details.startDate, details.endDate);
    const deliveryType = details.deliveryType;

    optionsArray.push({
      id: FULFILLMENT_TYPE.BODFS,
      title: `Scheduled Delivery${
        deliveryType?.toUpperCase() === 'OUTSIDE' ? ` (Outside)` : ''
      }`,
      deliveryCost: formatDeliveryCost(details.deliveryCharge, '+\u2060$'),
      dateLabel: date.isSameDay ? date.fullDate : date.startDate,
      description:
        'You can select a specific date for your items to be delivered, require a signature, and leave notes for the driver.',
      rawDeliveryCharge: parseFloat(details.deliveryCharge),
      rawStartDate: new Date(details.startDate),
    });
  }

  return sortDeliveryOptions(optionsArray);
};

const sortDeliveryOptions = (options: IDeliveryOption[]) => {
  return options.sort((optionA, optionB) => {
    // Order by charge
    if (optionA.rawDeliveryCharge !== optionB.rawDeliveryCharge) {
      return optionA.rawDeliveryCharge - optionB.rawDeliveryCharge;
    }

    // Order by startDate
    if (optionA.rawStartDate.getTime() !== optionB.rawStartDate.getTime()) {
      return optionA.rawStartDate.getTime() - optionB.rawStartDate.getTime();
    }

    // BODFS over STH
    if (
      optionA.id === FULFILLMENT_TYPE.BODFS &&
      optionB.id === FULFILLMENT_TYPE.STH
    ) {
      return -1;
    }
    if (
      optionA.id === FULFILLMENT_TYPE.STH &&
      optionB.id === FULFILLMENT_TYPE.BODFS
    ) {
      return 1;
    }

    return 0;
  });
};
